import {
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardHeader,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { AudienceSet, ChangeEventType, DemographicsSet } from '../../../types';
import Spinner from '../../Spinner';
import { DEFAULT_COLOR_THEME, ScrollStyle } from '../../../utils';
import { numberShortener } from '../../../utils/numberFormatter';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PrimaryButton } from '../../Buttons';
import AddIcon from '@mui/icons-material/Add';

interface DiyDemographicsSetFormProps {
  selectedAudienceSet: AudienceSet;
  demographicsSets: DemographicsSet[];
  filteredDemographicsSets: DemographicsSet[];
  setFilteredDemographicsSets: Dispatch<SetStateAction<DemographicsSet[]>>;
  onSelectDemographicsSet: (demographicsSet: DemographicsSet) => void;
  selectedDemographicsSet: DemographicsSet;
  setSelectedDemographicsSet: Dispatch<SetStateAction<DemographicsSet>>;
  reachError: string;
  reachLoading: boolean;
  reachEstimate: any;
  setOpenDemographicsSetForm: Dispatch<SetStateAction<boolean>>;
  setDemographicsSetAction: Dispatch<SetStateAction<'create' | 'edit'>>;
  fetchLoading: boolean;
}

const DiyDemographicsSetForm: FC<DiyDemographicsSetFormProps> = ({
  selectedAudienceSet,
  demographicsSets,
  filteredDemographicsSets,
  setFilteredDemographicsSets,
  onSelectDemographicsSet,
  selectedDemographicsSet,
  setSelectedDemographicsSet,
  reachError,
  reachLoading,
  reachEstimate,
  setOpenDemographicsSetForm,
  setDemographicsSetAction,
  fetchLoading,
}) => {
  const [search, setSearch] = useState<string>('');

  const isAIEnabled =
    selectedAudienceSet?.targets?.targeting_automation?.advantage_audience ===
    1;

  const handleOnSearch = (e: ChangeEventType) => {
    setSearch(e.target.value);
    const temp = demographicsSets.filter((demographicsSet: DemographicsSet) =>
      demographicsSet.name.toLowerCase().includes(e.target.value.toLowerCase()),
    );

    setFilteredDemographicsSets(temp);
  };

  const handleOnClickCreate = () => {
    setOpenDemographicsSetForm(true);
    setSelectedDemographicsSet(null);
    setDemographicsSetAction('create');
  };

  const handleOnClickEdit = (demographicsSet: DemographicsSet) => {
    setOpenDemographicsSetForm(true);
    setSelectedDemographicsSet(demographicsSet);
    setDemographicsSetAction('edit');
  };

  return (
    <Grid container spacing={2}>
      {isAIEnabled &&
      (selectedDemographicsSet?.age_min > 25 ||
        selectedDemographicsSet?.age_max < 65) ? (
        <Grid item xs={12} mb={2}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'red' }}>
            Advantage+ Audience Enabled: You can select a minimum age between 18
            and 25, and a maximum age of 65 or above.
          </Typography>
        </Grid>
      ) : null}

      <Grid
        item
        xs={12}
        sm={4}
        sx={{
          minHeight: `calc(100vh - ${isAIEnabled ? '300px' : '250px'})`,
          maxHeight: `calc(100vh - ${isAIEnabled ? '300px' : '250px'})`,
          overflowY: 'auto',
          overflowX: 'hidden',
          ...ScrollStyle(),
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 999,
            backgroundColor: 'white',
            mb: 2,
          }}
        >
          <Grid item xs={12}>
            <PrimaryButton
              title="Add Demographics Set"
              type="button"
              handleOnClick={handleOnClickCreate}
              startIcon={<AddIcon />}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              type="text"
              id="search"
              name="search"
              label="Search"
              onChange={handleOnSearch}
              value={search}
              InputLabelProps={{ shrink: true }}
              size="small"
              placeholder="Search by demographics set name"
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ px: 1, paddingY: '10px' }}>
          {!fetchLoading ? (
            filteredDemographicsSets?.length > 0 ? (
              filteredDemographicsSets.map(
                (demographicsSet: DemographicsSet) => {
                  const selected =
                    selectedDemographicsSet?._id === demographicsSet._id;
                  const disabled =
                    isAIEnabled &&
                    (demographicsSet.age_min > 25 ||
                      demographicsSet.age_max < 65);
                  let mainTextStyle: any = { fontWeight: 'bold' };
                  let subsStringStyle: any = { whiteSpace: 'pre-wrap' };
                  if (disabled) {
                    mainTextStyle.color = '#EBEBE4';
                    subsStringStyle.color = '#EBEBE4';
                  }

                  return (
                    <Grid
                      item
                      xs={12}
                      key={demographicsSet._id}
                      sx={{
                        position: 'relative',
                      }}
                    >
                      {selected ? (
                        <Box
                          component="div"
                          sx={{
                            position: 'absolute',
                            top: '8px',
                            zIndex: 1,
                            right: '-10px',
                            float: 'right',
                            margin: '1px',
                          }}
                        >
                          <CheckCircleIcon
                            sx={{
                              color: DEFAULT_COLOR_THEME,
                              bgcolor: 'InfoBackground',
                              borderRadius: '50%',
                            }}
                          />
                        </Box>
                      ) : null}

                      <Card
                        sx={{
                          height: '100%',
                          border: selected ? 'solid' : '',
                          borderColor: selected ? DEFAULT_COLOR_THEME : '',
                          borderWidth: 'medium',
                          zIndex: 0,
                        }}
                      >
                        <CardActionArea
                          onClick={() =>
                            onSelectDemographicsSet(demographicsSet)
                          }
                          disabled={disabled}
                        >
                          <CardHeader
                            title={demographicsSet.name}
                            subheader={`${
                              demographicsSet.description
                            }\nEst. Audience: ${numberShortener(
                              demographicsSet.minReach,
                            )} - ${numberShortener(demographicsSet.maxReach)}`}
                            titleTypographyProps={{
                              variant: 'body1',
                              sx: mainTextStyle,
                            }}
                            subheaderTypographyProps={{
                              variant: 'body2',
                              sx: subsStringStyle,
                            }}
                          />
                        </CardActionArea>

                        <CardActions
                          sx={{ justifyContent: 'flex-end', display: 'flex' }}
                        >
                          <PrimaryButton
                            title="Edit"
                            type="button"
                            variant="text"
                            handleOnClick={() =>
                              handleOnClickEdit(demographicsSet)
                            }
                          />
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                },
              )
            ) : (
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
                mt={2}
              >
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 'bold', textAlign: 'center' }}
                >
                  No data to show
                </Typography>

                <Typography variant="caption" sx={{ textAlign: 'center' }}>
                  Click Add Demographics Set to create a new one
                </Typography>
              </Grid>
            )
          ) : (
            <Grid
              item
              xs={12}
              sx={{ justifyContent: 'center', display: 'flex' }}
              mt={2}
            >
              <Spinner />
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12} sm={8}>
        <Grid container spacing={2}>
          <Grid item xs={12} sx={{ flexDirection: 'column', display: 'flex' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              Estimated Audience Size:{' '}
              <Typography
                component="span"
                variant="body1"
                sx={{ marginLeft: '10px' }}
              >
                {reachLoading ? (
                  <Spinner />
                ) : (
                  `${numberShortener(
                    reachEstimate.users_lower_bound,
                  )} - ${numberShortener(reachEstimate.users_upper_bound)}`
                )}
              </Typography>
            </Typography>

            {reachError ? (
              <Typography
                variant="caption"
                sx={{ fontWeight: 'bold', color: 'red' }}
              >
                {reachError}
              </Typography>
            ) : null}
          </Grid>

          {selectedDemographicsSet ? (
            <Grid
              item
              xs={12}
              sx={{ flexDirection: 'column', display: 'flex' }}
            >
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Genders:
                <Typography
                  component="span"
                  variant="body1"
                  sx={{ marginLeft: '10px' }}
                >
                  {`${
                    selectedDemographicsSet?.genders?.length > 1
                      ? 'All'
                      : selectedDemographicsSet?.genders[0] === 1
                      ? 'Male'
                      : 'Female'
                  }`}
                </Typography>
              </Typography>

              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Age Range:
                <Typography
                  component="span"
                  variant="body1"
                  sx={{ marginLeft: '10px' }}
                >
                  {`${selectedDemographicsSet?.age_min} - ${selectedDemographicsSet?.age_max}`}
                </Typography>
              </Typography>
            </Grid>
          ) : (
            <Grid
              item
              xs={12}
              sx={{ justifyContent: 'center', display: 'flex' }}
            >
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                No selected demographics set
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default DiyDemographicsSetForm;
